import { useTranslation } from '@jq4race/i18n';
import { Button, Input, Label } from '@jq4race/ui';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';

export interface StationSetupFormProps {
  submitting: boolean;
  errorMessage: string | null;
  onSubmit: (token: string) => void;
}

/**
 * One-time device pairing screen — spec "no personal valet login required":
 * the STATION DEVICE holds the credential (an opaque token an admin
 * provisions and hands to whoever sets up the terminal/tablet), not a staff
 * member's personal account. Submitted once per device; the resulting
 * session is cached (station-session-store.ts) so this form is not shown
 * again until the token is forgotten or a stored session/token becomes
 * invalid (e.g. the device was revoked).
 */
export function StationSetupForm({ submitting, errorMessage, onSubmit }: StationSetupFormProps) {
  const { t } = useTranslation('station');
  const [token, setToken] = useState('');
  // Lets the person pairing the device confirm what they pasted actually is the token.
  const [showToken, setShowToken] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (token.trim()) onSubmit(token.trim());
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
      <div>
        <h1 className="jq-h3 mb-1 text-foreground">{t('setup.title')}</h1>
        <p className="jq-body-sm text-muted-foreground">{t('setup.description')}</p>
      </div>
      <div>
        <Label htmlFor="station-device-token" className="mb-1.5 block text-[13px] font-medium">
          {t('setup.tokenLabel')}
        </Label>
        <div className="relative">
          <Input
            id="station-device-token"
            type={showToken ? 'text' : 'password'}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={t('setup.tokenPlaceholder')}
            className="pr-11"
          />
          <button
            type="button"
            onClick={() => setShowToken((v) => !v)}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-jq-graphite transition hover:text-jq-navy"
            aria-label={showToken ? t('setup.hideToken') : t('setup.showToken')}
            aria-pressed={showToken}
          >
            {showToken ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        </div>
      </div>
      {errorMessage ? (
        <p role="alert" className="font-sans text-sm text-destructive">
          {errorMessage}
        </p>
      ) : null}
      <Button type="submit" disabled={submitting || !token.trim()}>
        {submitting ? t('setup.connecting') : t('setup.connect')}
      </Button>
    </form>
  );
}
